import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Trophy } from 'lucide-react';

const WinnerHistory = () => {
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDraws();
  }, []);

  const loadDraws = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/draws');
      setDraws(data);
    } catch (error) {
      toast.error('Failed to load winner history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">
              <Trophy className="w-6 h-6 inline mr-2 text-[#D4AF37]" />
              Winner History
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <Card className="bg-[#121212] border border-[#D4AF37]/10">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#D4AF37]/20">
                  <TableHead className="text-[#D4AF37]">Draw #</TableHead>
                  <TableHead className="text-[#D4AF37]">Sugo ID</TableHead>
                  <TableHead className="text-[#D4AF37]">Username</TableHead>
                  <TableHead className="text-[#D4AF37]">Prize Won</TableHead>
                  <TableHead className="text-[#D4AF37]">Draw Time</TableHead>
                  <TableHead className="text-[#D4AF37]">Admin</TableHead>
                  <TableHead className="text-[#D4AF37]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#A0A0A0] py-8">
                      Loading winner history...
                    </TableCell>
                  </TableRow>
                ) : draws.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#A0A0A0] py-8">
                      No draws recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  draws.map((draw) => (
                    <TableRow key={draw.id} className="border-[#D4AF37]/10">
                      <TableCell className="text-white font-bold">#{draw.draw_number}</TableCell>
                      <TableCell className="text-[#D4AF37] font-mono">{draw.sugo_id}</TableCell>
                      <TableCell className="text-white">{draw.username || '-'}</TableCell>
                      <TableCell className="text-[#F3A6C8] font-semibold">{draw.prize_name}</TableCell>
                      <TableCell className="text-[#A0A0A0]">
                        {new Date(draw.draw_timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-[#A0A0A0] text-sm">{draw.admin_email}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            draw.result_status === 'Confirmed'
                              ? 'bg-green-600 text-white'
                              : 'bg-[#A0A0A0] text-[#080808]'
                          }
                        >
                          {draw.result_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {draws.length > 0 && (
          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6 mt-6">
            <h3 className="text-lg font-bold text-white mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-[#D4AF37]">{draws.length}</div>
                <div className="text-sm text-[#A0A0A0] mt-1">Total Draws</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#F3A6C8]">
                  {draws.filter((d) => d.result_status === 'Confirmed').length}
                </div>
                <div className="text-sm text-[#A0A0A0] mt-1">Confirmed Winners</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">
                  {new Set(draws.map((d) => d.sugo_id)).size}
                </div>
                <div className="text-sm text-[#A0A0A0] mt-1">Unique Winners</div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WinnerHistory;